import type { ColDef, ValueFormatterParams } from 'ag-grid-community';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import type { TypeFailItem } from '~/types/receive.types';

type TypeFailItemQty = TypeFailItem['boxQty'];

// isNaN 은 ''·'1e3'·'0x10' 도 숫자로 보아 입력과 다른 값을 보여주므로 십진수 표기만 숫자로 본다
const DECIMAL_TEXT_PATTERN = /^-?\d+(\.\d+)?$/;

const formatQtyNumber = (value: number) => (value ? formatNumber(value) : '-');

const formatFailItemQty = (value: TypeFailItemQty | null | undefined) => {
  // formatNumber 는 number 가 아니면 throw 하므로, 응답이 타입과 달라도 number 만 넘긴다
  if (typeof value === 'number') return formatQtyNumber(value);
  if (typeof value !== 'string') return '-';

  const text = value.trim();
  if (!text) return '-';
  return DECIMAL_TEXT_PATTERN.test(text) ? formatQtyNumber(Number(text)) : text;
};

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > 엑셀 추가 실패 모달 테이블
 */
const getReceiveExcelUploadFailModalColumnDefs = ({
  data,
  hasPermissionNFA,
}: {
  data: TypeFailItem[];
  hasPermissionNFA: boolean;
}) => {
  const columnDefs: ColDef<TypeFailItem>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
    },
    {
      field: 'goodsCode',
      headerName: '상품코드',
      width: 140,
    },
    {
      hide: !hasPermissionNFA,
      field: 'nsid',
      headerName: 'NSID',
      width: 180,
    },
    {
      field: 'manufactureDate',
      headerName: '제조일자',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeFailItem, TypeFailItem['manufactureDate']>) =>
        formatDate(value, 'YYYY.MM.DD') ?? '-',
    },
    {
      field: 'expirationDate',
      headerName: '유통/소비기한',
      width: 110,
      valueFormatter: ({ value }: ValueFormatterParams<TypeFailItem, TypeFailItem['expirationDate']>) =>
        formatDate(value, 'YYYY.MM.DD') ?? '-',
    },
    {
      field: 'palletYn',
      headerName: '파렛트 사용여부',
      width: 120,
    },
    {
      field: 'boxQty',
      headerName: '수량(BOX)',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeFailItem, TypeFailItemQty>) => formatFailItemQty(value),
    },
    {
      field: 'boxPerQty',
      headerName: '입수(BOX당)',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeFailItem, TypeFailItemQty>) => formatFailItemQty(value),
    },
    {
      field: 'eachQty',
      headerName: '수량(EA)',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeFailItem, TypeFailItemQty>) => formatFailItemQty(value),
    },
    {
      field: 'failReason',
      headerName: '실패사유',
      minWidth: 700,
      flex: 1,
    },
  ];

  return { columnDefs };
};

export default getReceiveExcelUploadFailModalColumnDefs;
